// @ts-nocheck
/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { 
    Info, 
    ThumbsUp, 
    ThumbsDown, 
    CheckCircle, 
    AlertTriangle, 
    XCircle, 
    ChevronDown,
    Lock,
    Unlock,
    Pencil,
    FileCheck,
    Upload,
    Loader2,
    FileText,
    Trash2,
    X,
    Download
} from 'lucide-react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { db, storage, auth } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { resolveEffectiveClientMwstSatz } from '../store/slices/settingsSlice';

// ============================================================================
// 1. STYLES & CONFIG
// ============================================================================

const CustomStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }

        @keyframes blink { 50% { border-color: #ef4444; box-shadow: 0 0 15px rgba(239, 68, 68, 0.6); } }
        .blinking-border { animation: blink 1.5s linear infinite; border: 2px solid transparent !important; }
        
        .unit-text { color: #64748b; font-weight: 800; font-size: 0.7rem; letter-spacing: 0.05em; text-transform: uppercase; font-family: 'Inter', sans-serif;}

        /* Carbon Weave Design */
        .carbon-weave-bg {
            background-color: #111;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #1a1a1a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
            overflow: hidden;
            z-index: 1;
        }

        .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
            pointer-events: none;
            z-index: 1;
        }

        .carbon-content {
            position: relative;
            z-index: 10;
        }

        /* TOOLTIP STYLES */
        [data-tooltip-text] { position: relative; }
        [data-tooltip-text]:hover::after {
            content: attr(data-tooltip-text);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            margin-bottom: 12px;
            background-color: #E6C299; color: #000000; border: 1px solid #000000;
            font-weight: 800; font-family: 'Inter', sans-serif;
            padding: 10px 14px; border-radius: 8px;
            white-space: normal; z-index: 1000; font-size: 0.75rem;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8); width: max-content;
            max-width: 250px; text-align: center; pointer-events: none;
        }
        [data-tooltip-text]:hover::before {
            content: '';
            position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
            margin-bottom: 2px; border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            z-index: 1000;
        }

        /* ===== LIGHT-MODE OVERRIDES (Warm-Platin) — Dark bleibt via html.dark unangetastet ===== */
        html:not(.dark) .carbon-weave-bg {
            background-color: var(--pe-bg-elevated);
            background-image: none;
        }
        html:not(.dark) .carbon-panel {
            border-color: var(--pe-border-subtle);
            box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
        }
        html:not(.dark) .carbon-panel::after { background: none; }
    `}</style>
);

// ============================================================================
// 2. UI-KOMPONENTEN
// ============================================================================

// --- DATEN-OVERLAY ---
const LockedOverlay = ({ onUnlockClick }: { onUnlockClick: () => void }) => (
    <div className="absolute inset-0 z-40 bg-stone-900/50 dark:bg-black/60 backdrop-blur-sm rounded-xl border border-pe-border dark:border-[#222] transition-all duration-300">
        <div className="sticky top-[35vh] mx-auto w-fit text-center p-8 bg-pe-sunken border border-pe-border-strong rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-200">
            <Lock className="w-10 h-10 text-stone-500 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-black text-pe-text mb-2 uppercase tracking-widest">Bearbeitungsmodus inaktiv</h3>
            <p className="text-pe-muted dark:text-gray-400 text-xs mb-6 max-w-xs mx-auto font-medium leading-relaxed">
                Zum Schutz vor versehentlichen Änderungen gesperrt.
            </p>
            <button 
                onClick={onUnlockClick}
                className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 px-8 rounded-xl shadow-[0_0_20px_rgba(234,88,12,0.4)] flex items-center gap-3 mx-auto transition-all text-xs uppercase tracking-widest border border-orange-500"
            >
                <Pencil size={16} />
                Bearbeiten
            </button>
        </div>
    </div>
);

const MonthConfirmationModal = ({ isOpen, month, monate, onMonthChange, onConfirm, onCancel }: any) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
            <div className="bg-pe-card border border-orange-500/50 rounded-2xl shadow-[0_0_50px_rgba(249,115,22,0.3)] max-w-md w-full p-8 transform scale-100 transition-all relative">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-600 via-orange-400 to-orange-600 rounded-t-2xl"></div>
                <div className="flex flex-col items-center text-center space-y-6 mt-2">
                    <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center mb-2 border border-orange-500/30">
                        <AlertTriangle className="w-10 h-10 text-orange-500 drop-shadow-md" />
                    </div>
                    
                    <div>
                        <h3 className="text-2xl font-black text-pe-text mb-3 uppercase tracking-widest">Sicherheits-Check</h3>
                        <p className="text-pe-muted dark:text-gray-400 text-sm leading-relaxed">
                            Du bist dabei, die Budget-Daten zu verändern.
                            <br/>Bitte bestätige den gewählten Zeitraum.
                        </p>
                    </div>

                    <div className="bg-pe-sunken rounded-xl p-5 w-full border border-pe-border dark:border-[#222] shadow-inner text-left">
                        <p className="text-[10px] text-stone-500 dark:text-gray-500 uppercase font-bold tracking-widest mb-2 text-center">Aktueller Monat</p>
                        <SelectCell 
                            value={month} 
                            onChange={onMonthChange}
                            options={monate}
                            placeholder="Monat wählen..."
                            className="!h-14 !text-lg !bg-pe-card !border-pe-border-strong !text-pe-accent-text"
                        />
                    </div>

                    <div className="flex flex-col w-full gap-3 pt-4">
                        <button 
                            onClick={onConfirm}
                            disabled={!month}
                            className={`w-full py-4 font-black rounded-xl transition-all transform flex items-center justify-center gap-3 uppercase tracking-widest text-xs border ${month ? 'bg-green-600 hover:bg-green-500 text-white shadow-[0_0_15px_rgba(22,163,74,0.4)] hover:scale-[1.02] border-green-500' : 'bg-stone-200 dark:bg-[#222] text-stone-500 dark:text-gray-500 border-pe-border-strong cursor-not-allowed'}`}
                        >
                            <Unlock size={16} />
                            {month ? `Ja, ${month} bearbeiten` : 'Bitte Monat wählen'}
                        </button>
                        <button 
                            onClick={onCancel}
                            className="w-full py-4 bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] text-pe-muted dark:text-gray-400 font-bold rounded-xl transition-colors border border-pe-border-strong uppercase tracking-widest text-xs"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Input: Black Background, White Text
const InputCell = ({ value, onChange, disabled = false, isLocked = false, placeholder = "0,00", jahresbudget = 0, itemId = '', currency = 'EUR' }: any) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (/^\d*[.,]?\d*$/.test(val)) {
            setLocalValue(val);
        }
    };

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    const showWarning = !disabled && !isLocked && jahresbudget > 0 && (localValue === '' || localValue === null || localValue === undefined);
    
    let borderClass = "border-pe-border-strong";
    if (showWarning) {
        borderClass = "border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)] animate-pulse";
    } else if (isLocked) {
        borderClass = "border-[#222] bg-pe-tile-header"; 
    } else {
        borderClass = "border-pe-border-strong focus-within:border-[#E6C299] focus-within:ring-1 focus-within:ring-[#E6C299] bg-pe-input dark:bg-[#000] shadow-inner";
    }

    let tooltipText = "Wirklich leer? Falls der Kostenfaktor sicher wegfällt, trage hier 1 ein.";
    if (itemId === 'verbindlichkeiten') {
        tooltipText = "Diesen Wert findest du in der BWA oft nicht unter den Kosten! Trage hier deinen monatlichen Abtrag × der berechneten Monate (BWA) ein.";
    }

    return (
        <div className={`relative w-full h-[42px] flex items-center border rounded-lg transition-colors ${borderClass} group hover:z-50`}>
            <input
                type="text"
                inputMode="decimal"
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onFocus={(e) => e.currentTarget.select()}
                disabled={disabled || isLocked}
                className={`w-full h-full bg-transparent text-center outline-none font-mono text-lg placeholder-gray-600 
                    ${disabled ? 'opacity-50 cursor-not-allowed text-stone-500 dark:text-gray-500' : ''}
                    ${isLocked ? 'cursor-default text-stone-500 dark:text-gray-500 font-medium select-none' : 'text-pe-text font-bold'}
                `}
                placeholder={isLocked ? "" : placeholder}
            />
            {(!disabled && !isLocked) && (
                <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                    <span className="unit-text">{currency}</span>
                </div>
            )}

            {showWarning && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 p-3 bg-[#E6C299] text-black text-xs font-bold text-center rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 leading-relaxed border border-black font-sans">
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-b-[#E6C299]"></div>
                    {tooltipText}
                </div>
            )}
        </div>
    );
};

const ResultCell = ({ value, isPercent = false, isNegativeRed = false, currency = 'EUR' }: any) => {
    const isNegative = value < 0;
    const textColorClass = (isNegativeRed && isNegative) ? 'text-red-600 dark:text-red-400' : 'text-pe-accent-text';
    const displayValue = isPercent 
        ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value) 
        : new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(value || 0));
    const unit = isPercent ? '%' : currency;

    return (
        <div className="relative w-full h-[42px] bg-pe-sunken border border-pe-border dark:border-[#222] rounded-lg flex items-center justify-center shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] px-2">
            <span className={`font-mono text-lg font-bold ${textColorClass} mr-2 drop-shadow-md`}>
                {displayValue}
            </span>
            <span className="unit-text">{unit}</span>
        </div>
    );
};

const SelectCell = ({ value, onChange, options, placeholder = "Bitte wählen...", className = "" }: any) => (
    <div className="relative">
        <select 
            value={value} 
            onChange={onChange}
            className={`w-full h-12 bg-pe-input dark:bg-[#000] text-pe-accent-text text-center font-bold pl-4 pr-10 rounded-xl border border-pe-border-strong outline-none focus:ring-1 focus:ring-[#E6C299] focus:border-[#E6C299] cursor-pointer appearance-none shadow-inner tracking-widest uppercase text-sm ${className}`}
        >
            <option value="" className="bg-pe-card text-stone-500 dark:text-gray-500">{placeholder}</option>
            {options.map((opt: string) => (
                <option key={opt} value={opt} className="bg-pe-card text-pe-text font-bold">{opt}</option>
            ))}
        </select>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-pe-accent-text">
            <ChevronDown size={18} />
        </div>
    </div>
);

const Section = ({ children, className = '' }: any) => <section className={`border border-pe-border dark:border-[#222] rounded-2xl overflow-hidden ${className}`}>{children}</section>;
const SectionTitle = ({ children, className = '' }: any) => <h2 className={`text-lg font-bold p-4 bg-pe-sunken text-pe-accent-text border-b border-pe-border dark:border-[#222] uppercase tracking-widest ${className}`}>{children}</h2>;

// ============================================================================
// 3. LOGIK & BERECHNUNG
// ============================================================================

const useBudgetCalculations = (
    kostenData: any[], 
    zeitraum: string, 
    angenommenerGesamtumsatzProMonat: number, 
    gesamtleistungData: any[], 
    lohnfaktorData: any, 
    mwstFaktor: number
) => {
    const gesamtleistungStats = useMemo(() => {
        const mindestumsatz1 = lohnfaktorData?.mindestumsatz1 || 0;
        const mindestumsatz2 = lohnfaktorData?.mindestumsatz2 || 0;
        
        let sumRuecklagenDelta = 0;
        let activeMonths = 0;

        gesamtleistungData.forEach((monat: any, index: number) => {
            const isActive = monat.bedienumsatzBrutto > 0 || monat.vkUmsatzBrutto > 0 || monat.abgeschlossen;
            if (isActive) {
                activeMonths++;
                const gesamtBrutto = (monat.bedienumsatzBrutto || 0) + (monat.vkUmsatzBrutto || 0);
                const gesamtNetto = gesamtBrutto / mwstFaktor;
                const currentBaseMindestumsatz = index < 6 ? mindestumsatz1 : mindestumsatz2;
                const anpassungsFaktor = monat.kostenErhoeht ? -1 : 1;
                const angepassterMindestumsatz = currentBaseMindestumsatz + ((monat.aussergewoehnlich || 0) * anpassungsFaktor);
                const ruecklagenDelta = gesamtNetto - angepassterMindestumsatz;
                sumRuecklagenDelta += ruecklagenDelta;
            }
        });

        const safeMonths = activeMonths || 1;
        const avgRuecklagenDelta = activeMonths > 0 ? sumRuecklagenDelta / safeMonths : 0;
        
        let sumProzentWerte = 0;
        gesamtleistungData.forEach((monat: any, index: number) => {
             const isActive = monat.bedienumsatzBrutto > 0 || monat.vkUmsatzBrutto > 0 || monat.abgeschlossen;
             if (isActive) {
                const gesamtBrutto = (monat.bedienumsatzBrutto || 0) + (monat.vkUmsatzBrutto || 0);
                const gesamtNetto = gesamtBrutto / mwstFaktor;
                const currentBaseMindestumsatz = index < 6 ? mindestumsatz1 : mindestumsatz2;
                const anpassungsFaktor = monat.kostenErhoeht ? -1 : 1;
                const angepassterMindestumsatz = currentBaseMindestumsatz + ((monat.aussergewoehnlich || 0) * anpassungsFaktor);
                const ruecklagenDelta = gesamtNetto - angepassterMindestumsatz;
                const prozent = angepassterMindestumsatz !== 0 ? ruecklagenDelta / angepassterMindestumsatz : 0;
                sumProzentWerte += prozent;
             }
        });
        
        const avgProzent = activeMonths > 0 ? sumProzentWerte / safeMonths : 0;

        return {
            avgRuecklagenDelta, 
            avgProzent: avgProzent * 100 
        };
    }, [gesamtleistungData, lohnfaktorData, mwstFaktor]);


    return useMemo(() => {
        const basisRuecklagenMonat = gesamtleistungStats.avgRuecklagenDelta;
        const basisRuecklagenProzent = gesamtleistungStats.avgProzent;

        if (!zeitraum) {
            return {
                berechneteDaten: kostenData.map(item => ({...item, monatsbudget: item.jahresbudget / 12, hatEingabe: false})),
                hasAnyInput: false,
                anzahlMonateBisher: 0,
                totalPlusMinusMonat: 0,
                plusMinusBudgetInsgesamt: 0,
                basisRuecklagenMonat,
                basisRuecklagenProzent,
                realeRuecklagenMonat: basisRuecklagenMonat, 
                realeRuecklagenInsgesamt: 0,
                realeRuecklagenProzent: basisRuecklagenProzent,
            };
        }
        
        const monate = ["Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November"];
        const monatsIndex = monate.indexOf(zeitraum);
        const anzahlMonateBisher = monatsIndex + 2;
        let totalPlusMinusMonat = 0;

        const berechneteDaten = kostenData.map(item => {
            const monatsbudget = item.jahresbudget / 12;
            const cleanVal = item.istwertBwa ? item.istwertBwa.replace(',', '.') : '';
            const istwertBwaNum = parseFloat(cleanVal);
            const hatEingabe = !isNaN(istwertBwaNum) && item.istwertBwa !== '' && item.istwertBwa !== undefined;

            if (!hatEingabe) {
                return { 
                    ...item, 
                    monatsbudget, 
                    istwertBwaNum: '',
                    restBudgetJahr: item.jahresbudget, 
                    restBudgetMonat: monatsbudget, 
                    plusMinusMonat: 0,
                    hatEingabe: false
                };
            }
            
            const budgetBisher = monatsbudget * anzahlMonateBisher;
            const differenz = budgetBisher - istwertBwaNum;
            const plusMinusMonat = differenz / anzahlMonateBisher;
            const restBudgetJahr = item.jahresbudget - istwertBwaNum;
            const verbleibendeMonate = 12 - anzahlMonateBisher;
            const restBudgetMonat = verbleibendeMonate > 0 ? restBudgetJahr / verbleibendeMonate : 0;

            const plusMinusFuerSumme = item.isIncome ? -plusMinusMonat : plusMinusMonat;
            totalPlusMinusMonat += plusMinusFuerSumme;
            return { ...item, monatsbudget, istwertBwaNum: item.istwertBwa, restBudgetJahr, restBudgetMonat, plusMinusMonat, hatEingabe: true };
        });

        const plusMinusBudgetInsgesamt = totalPlusMinusMonat * anzahlMonateBisher;
        
        const realeRuecklagenMonat = basisRuecklagenMonat + totalPlusMinusMonat;
        const realeRuecklagenInsgesamt = realeRuecklagenMonat * anzahlMonateBisher;
        
        const prozentEffektSparmassnahme = angenommenerGesamtumsatzProMonat > 0 ? (totalPlusMinusMonat / angenommenerGesamtumsatzProMonat) * 100 : 0;
        const realeRuecklagenProzent = basisRuecklagenProzent + prozentEffektSparmassnahme;
        
        const hasAnyInput = kostenData.some(item => item.istwertBwa && item.istwertBwa.trim() !== '');
        
        return {
            berechneteDaten,
            totalPlusMinusMonat,
            plusMinusBudgetInsgesamt,
            basisRuecklagenMonat,
            basisRuecklagenProzent,
            realeRuecklagenMonat,
            realeRuecklagenInsgesamt,
            realeRuecklagenProzent,
            anzahlMonateBisher,
            hasAnyInput,
        };
    }, [kostenData, zeitraum, gesamtleistungStats, angenommenerGesamtumsatzProMonat]);
};

// ============================================================================
// 4. FEEDBACK & SUB-COMPONENTS
// ============================================================================

const StatusIndicator = ({ percentage }: { percentage: number }) => {
    if (percentage >= 10) {
        return <CheckCircle className="h-6 w-6 text-green-500 drop-shadow-md" strokeWidth={2.5} />;
    } else if (percentage >= 0) {
        return <AlertTriangle className="h-6 w-6 text-yellow-500 drop-shadow-md" strokeWidth={2.5} />;
    } else {
        return <XCircle className="h-6 w-6 text-red-500 drop-shadow-md" strokeWidth={2.5} />;
    }
};

const DynamicFeedback = ({ totalPlusMinusMonat, realeRuecklagenProzent, externeRuecklagenProzent, hasAnyInput, currency, targetLiquiditaetProzent }: any) => {
    if (!hasAnyInput) {
        return <p className="text-stone-500 dark:text-gray-500 uppercase tracking-widest text-xs font-bold">Gib deine Ist-Werte aus der BWA ein, um eine detaillierte Auswertung zu erhalten.</p>;
    }

    const formatCurrency = (value: number) => {
        const num = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
        return `${num} ${currency}`;
    };

    const targetPercentStr = new Intl.NumberFormat('de-DE').format(targetLiquiditaetProzent);

    const hasSavedMoney = totalPlusMinusMonat > 0;
    const hasOverspent = totalPlusMinusMonat < 0;
    const isExactlyOnBudget = totalPlusMinusMonat === 0;
    const isAboveTarget = realeRuecklagenProzent >= targetLiquiditaetProzent;
    const wasInitiallyAboveTarget = externeRuecklagenProzent >= targetLiquiditaetProzent;

    if (realeRuecklagenProzent < 0) {
        return (
            <div className="bg-red-950/30 border border-red-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                <p className="font-bold text-red-400 uppercase tracking-widest text-xs mb-1">Achtung: Deine realen Rücklagen sind im negativen Bereich!</p>
                <p className="text-red-300 font-medium text-sm">Hier besteht dringender Handlungsbedarf. Bitte setze dich umgehend mit deinem Betreuer in Verbindung.</p>
            </div>
        );
    }

    if (isAboveTarget) {
        if (!wasInitiallyAboveTarget && hasSavedMoney) {
             return (
                <div className="bg-green-950/30 border border-green-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-pe-accent-text uppercase tracking-widest text-xs mb-1">Wow, super gemacht! 👍</p>
                    <p className="text-green-400 font-medium text-sm">Obwohl dein Planwert unter {targetPercentStr}% lag, hast du durch Kosteneinsparungen das Ziel erreicht!</p>
                </div>
            );
        }
        if (hasSavedMoney) {
            return (
                <div className="bg-green-950/30 border border-green-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-pe-text uppercase tracking-widest text-xs mb-1">Du hast {formatCurrency(totalPlusMinusMonat)} pro Monat gespart und deine Rücklagen sind im grünen Bereich.</p>
                    <p className="text-green-400 font-medium text-sm">Alles ist gut! 👍</p>
                </div>
            );
        } 
        if (hasOverspent) {
            return (
                <div className="bg-yellow-950/30 border border-yellow-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-yellow-500 uppercase tracking-widest text-xs mb-1">Achtung: Du hast {formatCurrency(Math.abs(totalPlusMinusMonat))} pro Monat mehr ausgegeben als geplant.</p>
                    <p className="text-green-400 font-medium text-sm">Dank guter Planung sind deine Rücklagen aber noch im grünen Bereich (über {targetPercentStr}%). Behalte die Kosten trotzdem im Auge!</p>
                </div>
            );
        }
        if (isExactlyOnBudget) {
            return <p className="font-bold text-green-500 uppercase tracking-widest text-xs">Perfekt! Deine Kosten entsprechen exakt dem Budget und deine Rücklagen sind im Zielbereich.</p>
        }
    }
    
    if (!isAboveTarget) {
        if(hasSavedMoney) {
           return (
                 <div className="bg-yellow-950/30 border border-yellow-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-yellow-500 uppercase tracking-widest text-xs mb-1">Deine Rücklagen sind zwar unter dem Ziel von {targetPercentStr}%, aber du hast Kosten eingespart. Sehr gut!</p>
                    <p className="text-pe-text font-medium text-sm">Das ist der richtige Weg. Lass uns gemeinsam daran arbeiten.</p>
                </div>
            )
        } 
        if (hasOverspent) {
            return (
                <div className="bg-yellow-950/30 border border-yellow-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-yellow-500 uppercase tracking-widest text-xs mb-1">Deine Rücklagen sind unter dem Ziel von {targetPercentStr}%, da du mehr Kosten als geplant hattest.</p>
                    <p className="text-pe-text font-medium text-sm">Lass uns gemeinsam daran arbeiten, das zu optimieren.</p>
                </div>
            );
        }
        if (isExactlyOnBudget) {
            return (
                <div className="bg-yellow-950/30 border border-yellow-900/50 p-4 rounded-xl shadow-inner inline-block text-left">
                    <p className="font-bold text-yellow-500 uppercase tracking-widest text-xs mb-1">Deine Kosten entsprechen exakt dem Budget, aber deine geplanten Rücklagen liegen unter dem Ziel von {targetPercentStr}%.</p>
                    <p className="text-pe-text font-medium text-sm">Setze dich mit deinem Betreuer in Verbindung, um zu prüfen, wie ihr die Rentabilität steigern könnt.</p>
                </div>
            );
        }
    }

    return null;
};

// ============================================================================
// 5. MAIN PAGE COMPONENT
// ============================================================================

const BudgetPlanerPage: React.FC = () => {
    const { 
        clientData, 
        settings, 
        updateNestedClientData,
        setClientData,
        updateYearBwaUrl
    } = useAppStore(state => ({
        clientData: state.clientData,
        settings: state.settings,
        updateNestedClientData: state.updateNestedClientData,
        setClientData: state.setClientData,
        updateYearBwaUrl: state.updateYearBwaUrl
    }));
    
    const { impersonatedClientId } = useAdminStore();

    // ------------------------------------------------------------------------
    // BWA UPLOAD & VERWALTUNG STATE
    // ------------------------------------------------------------------------
    const [isUploading, setIsUploading] = useState(false);
    const [isDeletingUrl, setIsDeletingUrl] = useState<string | null>(null);
    const [yearNeedingUpload, setYearNeedingUpload] = useState<number | null>(null);
    const [showBwaModal, setShowBwaModal] = useState(false);

    const bwaCurrentYearStr = localStorage.getItem('CURRENT_YEAR') || new Date().getFullYear().toString();
    const geschaeftsjahre = clientData?.geschaeftsjahre || [];
    const targetYearForModal = geschaeftsjahre.find((j: any) => String(j.jahr) === String(yearNeedingUpload));
    const modalHasBwa = targetYearForModal?.bwaUrl || (targetYearForModal?.bwaUrls && targetYearForModal.bwaUrls.length > 0);
    const modalBwaList = targetYearForModal?.bwaUrls || (targetYearForModal?.bwaUrl ? [targetYearForModal.bwaUrl] : []);

    const handleLateBwaUpload = async (e: React.ChangeEvent<HTMLInputElement>, targetYear: number | null) => {
        if (!targetYear) return;

        try {
            const files = Array.from(e.target.files || []);
            if (files.length === 0) return;

            const allValid = files.every(f => f.type === 'application/pdf');
            if (!allValid) {
                alert("Bitte lade ausschließlich originale PDF-Dokumente (keine Fotos) hoch.");
                if (e.target) e.target.value = '';
                return;
            }

            setIsUploading(true);
            
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const newUrls: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const timestamp = Date.now();
                const fileRef = ref(storage, `kalkulation_mandanten/${safeUid}/uploads/BWA_${safeYear}_${timestamp}_${i}.pdf`);
                
                const metadata = { contentType: 'application/pdf' };
                const snapshot = await uploadBytes(fileRef, file, metadata);
                const downloadUrl = await getDownloadURL(snapshot.ref);
                newUrls.push(downloadUrl);
            }

            const finalUrls = [...existingBwaUrls, ...newUrls];

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: finalUrls[0], bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: finalUrls[0],
                bwaUrls: finalUrls,
                "clientData.bwaUrl": finalUrls[0],
                "clientData.bwaUrls": finalUrls
            }, { merge: true });

            if (typeof setClientData === 'function') {
                setClientData((prev: any) => {
                    if (!prev) return prev;
                    return { ...prev, geschaeftsjahre: updatedJahre };
                });
            }

            if (typeof updateYearBwaUrl === 'function') {
                updateYearBwaUrl(targetYear, finalUrls[0]);
            }

            alert(`${files.length} BWA-Dokument(e) für ${targetYear} erfolgreich hochgeladen!`);
        } catch (err) {
            console.error("Upload Fehler:", err);
            alert("Fehler beim Upload.");
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleDeleteSingleBwa = async (urlToDelete: string, targetYear: number | null) => {
        if (!targetYear || !urlToDelete) return;
        if (!window.confirm("Dieses PDF wirklich unwiderruflich löschen?")) return;

        setIsDeletingUrl(urlToDelete);
        try {
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            try {
                const fileRef = ref(storage, urlToDelete);
                await deleteObject(fileRef);
            } catch (storageErr) {
                console.warn("Storage-Löschung fehlgeschlagen (evtl. schon gelöscht):", storageErr);
            }

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const finalUrls = existingBwaUrls.filter((u: string) => u !== urlToDelete);
            const newMainUrl = finalUrls.length > 0 ? finalUrls[0] : '';

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: newMainUrl, bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: newMainUrl,
                bwaUrls: finalUrls,
                "clientData.bwaUrl": newMainUrl,
                "clientData.bwaUrls": finalUrls
            }, { merge: true });

            if (typeof setClientData === 'function') {
                setClientData((prev: any) => {
                    if (!prev) return prev;
                    return { ...prev, geschaeftsjahre: updatedJahre };
                });
            }

            alert("PDF erfolgreich gelöscht.");
        } catch (err) {
            console.error("Fehler beim Löschen:", err);
            alert("Fehler beim Löschen der Datei.");
        } finally {
            setIsDeletingUrl(null);
        }
    };

    const handleOpenBwa = (targetUrl: string) => {
        if (!targetUrl) return;
        const cleanUrl = targetUrl + '&view=FitH#toolbar=0&navpanes=0';
        window.open(cleanUrl, '_blank', 'width=1000,height=900,left=100,top=100,resizable=yes,scrollbars=yes');
    };
    // ------------------------------------------------------------------------

    const currency = settings.country?.currency || 'EUR';
    const mwstFaktor = 1 + resolveEffectiveClientMwstSatz(settings, clientData) / 100;
    const targetLiquiditaetProzent = settings.global?.Liquiditaet ?? 10;

    const storedEntries = useMemo(() => {
        return clientData.budgetPlaner?.entries || [];
    }, [clientData.budgetPlaner?.entries]);

    const zeitraum = clientData.budgetPlaner?.zeitraum || '';
    
    const monthSelected = !!zeitraum;
    const monate = ["Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November"];

    const activeYearData = clientData?.geschaeftsjahre?.find((j: any) => String(j.jahr) === bwaCurrentYearStr);
    const rawBwaUrls = activeYearData?.bwaUrls || clientData.bwaUrls || clientData.bwaData?.bwaUrls;
    const rawBwaSingle = activeYearData?.bwaUrl || clientData.bwaUrl || clientData.bwaData?.bwaUrl;
    
    let bwaList: string[] = [];
    if (Array.isArray(rawBwaUrls) && rawBwaUrls.length > 0) {
        bwaList = rawBwaUrls;
    } else if (typeof rawBwaSingle === 'string' && rawBwaSingle.trim() !== '') {
        bwaList = [rawBwaSingle];
    } else if (Array.isArray(rawBwaSingle) && rawBwaSingle.length > 0) {
        bwaList = rawBwaSingle;
    }

    // STATE FÜR SICHERHEIT
    const [isEditing, setIsEditing] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        setIsEditing(false);
    }, []);

    const handleZeitraumChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        updateNestedClientData('budgetPlaner', 'zeitraum', val);
        
        setIsEditing(false);
    };

    const handleUnlockRequest = () => {
        setShowConfirmation(true);
    };

    const handleUnlockClick = () => {
        if (isEditing) {
            setIsEditing(false); 
        } else {
            setShowConfirmation(true); 
        }
    };

    const confirmUnlock = () => {
        setShowConfirmation(false);
        setIsEditing(true);
    };

    const handleIstwertChange = (id: string, value: string) => {
        const newEntries = [...storedEntries];
        const existingIndex = newEntries.findIndex((e: any) => e.id === id);

        if (existingIndex >= 0) {
            newEntries[existingIndex] = { id, istwertBwa: value };
        } else {
            newEntries.push({ id, istwertBwa: value });
        }

        updateNestedClientData('budgetPlaner', 'entries', newEntries);
    };

    const bwaData = useMemo(() => {
        return clientData.bwaData || {};
    }, [clientData.bwaData]);

    const kostenData = useMemo(() => {
        const baseRows = [
            { id: 'material', bwaKey: 'material', label: 'Material', isVariabel: true },
            { id: 'raumkosten', bwaKey: 'raumkosten', label: 'Raumkosten' },
            { id: 'betriebssteuern', bwaKey: 'betriebssteuern', label: 'Betriebliche Steuern' },
            { id: 'versicherungen', bwaKey: 'versicherungen', label: 'Versicherungen/Beiträge/Steuern' },
            { id: 'kfzKosten', bwaKey: 'kfzKosten', label: 'KFZ-Kosten' },
            { id: 'werbeReisekosten', bwaKey: 'werbeReisekosten', label: 'Werbe- und Reisekosten', isVariabel: true },
            { id: 'warenabgabe', bwaKey: 'warenabgabe', label: 'Warenabgabe' },
            { id: 'reparatur', bwaKey: 'reparatur', label: 'Reparatur/Instandhaltung' },
            { id: 'sonstigeKosten', bwaKey: 'sonstigeKosten', label: 'Sonstige/verschiedene Kosten' },
            { id: 'verbindlichkeiten', bwaKey: 'kredite', label: 'Verbindlichkeiten/Kreditraten', isVariabel: true },
            { id: 'zinsaufwendungen', bwaKey: 'zinsaufwendungen', label: 'Zinsaufwendungen' },
            { id: 'gewerbesteuer', bwaKey: 'gewerbesteuer', label: 'Gewerbesteuer' },
            { id: 'koerperschaftssteuer', bwaKey: 'koerperschaftssteuer', label: 'Körperschaftssteuer (GmbH)' },
            { id: 'sonstigeEintrag1', bwaKey: 'sonstigeEintrag1', label: 'Sonstige Einträge' },
            { id: 'sonstigeEintrag2', bwaKey: 'sonstigeEintrag2', label: 'Sonstige Einträge' },
            { id: 'sonstigeEintrag3', bwaKey: 'sonstigeEintrag3', label: 'Sonstige Einträge' },
            { id: 'sonstigeEintrag4', bwaKey: 'sonstigeEintrag4', label: 'Sonstige Einträge' },
        ];

        const deviceRows = [];
        const deviceCount = bwaData.visibleGeraeteCount || 0;
        for (let i = 1; i <= deviceCount; i++) {
            deviceRows.push({
                id: `geraetEintrag${i}`,
                bwaKey: `geraetEintrag${i}`,
                label: 'Gerät', 
                isVariabel: false
            });
        }

        const bottomRows = [
            { id: 'budgetPlansumme', bwaKey: 'budgetPlansumme', label: 'Budget-Plansumme', isVariabel: true },
            { id: 'abschreibungen', bwaKey: 'abschreibungen', label: 'Abschreibungen' },
            { id: 'zinsertraege', bwaKey: 'zinsenMiete', label: 'Zinserträge - Mieteinnahmen - Sonstige Einnahmen', isIncome: true },
        ];

        const fullStructure = [...baseRows, ...bottomRows, ...deviceRows];

        return fullStructure.map(row => {
            const storedEntry = storedEntries.find((e: any) => e.id === row.id);
            const jahresbudget = bwaData[row.bwaKey] || 0;
            
            let dynamicLabel = row.label;
            
            if (row.id.startsWith('sonstigeEintrag')) {
                dynamicLabel = bwaData[`${row.bwaKey}_label`] || row.label;
            } else if (row.id.startsWith('geraetEintrag')) {
                dynamicLabel = bwaData[`${row.bwaKey}_label`] || `Gerät ${row.id.replace('geraetEintrag', '')}`;
            }

            return { 
                ...row,
                label: dynamicLabel,
                jahresbudget, 
                istwertBwa: storedEntry ? storedEntry.istwertBwa : '' 
            };
        });
    }, [storedEntries, bwaData]);

    const angenommenerGesamtumsatzProMonat = clientData.lohnfaktorData?.mindestumsatz1 || 10000; 

    const gesamtleistungData = useMemo(() => clientData.gesamtleistungData || [], [clientData.gesamtleistungData]);
    const lohnfaktorData = useMemo(() => clientData.lohnfaktorData || {}, [clientData.lohnfaktorData]);

    const calculations = useBudgetCalculations(
        kostenData, 
        zeitraum, 
        angenommenerGesamtumsatzProMonat,
        gesamtleistungData,
        lohnfaktorData,
        mwstFaktor
    );
    
    const monatsInfoText = "Dieses Tool dient der laufenden unterjährigen Kontrolle und Anpassung deines Budgets. Der Januar dient als Ausgangsbasis und hat noch keine Vergleichswerte aus Vormonaten. Der Dezember wird nicht mehr aktiv geplant, da hier das Geschäftsjahr bereits abgeschlossen ist und die Werte in die Jahresendauswertung fließen. Der Fokus liegt also auf den anpassbaren Monaten Februar bis November.";
    
    const formatEUR = (val: number) => {
        return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
    };

    return (
        <div className="min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-muted dark:text-gray-300 font-sans pb-32 relative">
            <CustomStyles />
            
            <MonthConfirmationModal 
                isOpen={showConfirmation} 
                month={zeitraum || ""} 
                monate={monate}
                onMonthChange={handleZeitraumChange}
                onConfirm={confirmUnlock}
                onCancel={() => setShowConfirmation(false)}
            />

            {/* BWA UPLOAD MODAL OVERLAY */}
            {yearNeedingUpload !== null && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="carbon-panel carbon-weave-bg border border-pe-border-strong rounded-2xl shadow-2xl w-full max-w-2xl relative overflow-hidden p-8 flex flex-col gap-6">
                        <div className={`absolute top-0 left-0 w-1 h-full ${modalHasBwa ? 'bg-blue-500' : 'bg-red-500'}`}></div>
                        
                        <div className="carbon-content flex justify-between items-start">
                            <div>
                                <h3 className="text-pe-text font-bold text-2xl mb-2 flex items-center gap-3">
                                    {modalHasBwa ? (
                                        <><FileCheck className="w-7 h-7 text-blue-400" /> BWA-Verwaltungs-Hub (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    ) : (
                                        <><AlertTriangle className="w-7 h-7 text-red-500" /> BWA Fehlt! (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    )}
                                </h3>
                                <p className="text-pe-muted dark:text-slate-400">
                                    {modalHasBwa 
                                        ? `Hier siehst du alle hochgeladenen Dokumente. Du kannst falsche PDFs löschen oder über das Feld unten weitere anhängen.`
                                        : `Lade hier deine aktuelle BWA hoch. Für den Jahresabschluss benötigen wir zwingend die finale Dezember-BWA des Vorjahres. Du kannst hier auch mehrere PDFs auf einmal markieren.`}
                                </p>
                            </div>
                            <button onClick={() => setYearNeedingUpload(null)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text p-2 metal-badge transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {modalHasBwa && modalBwaList.length > 0 && (
                            <div className="carbon-content bg-pe-input dark:bg-black border border-pe-border-strong rounded-xl p-4 flex flex-col gap-2">
                                <h4 className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-bold mb-2">Hochgeladene Dateien:</h4>
                                {modalBwaList.map((url: string, idx: number) => (
                                    <div key={idx} className="flex items-center justify-between bg-pe-card border border-pe-border dark:border-[#222] p-3 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-blue-400" />
                                            <span className="text-sm font-bold font-mono-numbers text-pe-muted dark:text-slate-300">BWA_Dokument_Teil_{idx + 1}.pdf</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => handleOpenBwa(url)}
                                                className="text-blue-400 hover:text-blue-300 p-2 bg-blue-950/20 hover:bg-blue-950/40 border border-blue-500/20 rounded transition-colors"
                                                title="BWA Anzeigen"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM0,160H40V56H216V200ZM176,88a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,88Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,128Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,168Z"></path>
                                                </svg>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteSingleBwa(url, yearNeedingUpload)}
                                                disabled={isDeletingUrl === url || isUploading}
                                                className="text-red-500 hover:text-red-400 p-2 bg-red-950/20 hover:bg-red-950/40 border border-red-500/20 rounded transition-colors"
                                                title="Datei unwiderruflich löschen"
                                            >
                                                {isDeletingUrl === url ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!modalHasBwa && (
                            <div className="carbon-content bg-red-950/20 border border-red-900/50 rounded-xl p-5 shadow-inner">
                                <ul className="text-pe-muted dark:text-slate-300 text-sm space-y-2 list-disc list-inside pl-2 marker:text-red-500/70">
                                    <li>Lade für den Jahresabschluss die <strong>Dezember-BWA</strong> des Vorjahres hoch, oder bei Bedarf deine <strong>aktuelle unterjährige BWA</strong>.</li>
                                    <li>Die BWA muss zwingend den <strong>Wertenachweis</strong> (Kontennachweis) enthalten <strong>(keine Summen- und Saldenliste!)</strong>. Schau dir dazu bitte die Muster-BWA an.</li>
                                    <li><strong>Keine Fotos!</strong> Wir benötigen das originale PDF deines Steuerberaters.</li>
                                </ul>
                            </div>
                        )}

                        <div className="carbon-content w-full h-32 relative group">
                            <input 
                                id="bwa-upload-input"
                                type="file" 
                                accept=".pdf" 
                                multiple
                                onChange={(e) => handleLateBwaUpload(e, yearNeedingUpload)}
                                disabled={isUploading}
                                className="hidden" 
                            />
                            <label 
                                htmlFor="bwa-upload-input"
                                className={`w-full h-full border-2 border-dashed rounded-2xl transition-all flex flex-col items-center justify-center cursor-pointer 
                                    ${modalHasBwa ? 'border-blue-900/50 bg-blue-950/10 hover:border-blue-500' : 'border-red-900/50 bg-red-950/20 hover:border-red-500'} 
                                    ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                                {isUploading ? (
                                    <Loader2 className={`w-8 h-8 animate-spin mb-2 ${modalHasBwa ? 'text-blue-400' : 'text-red-400'}`} />
                                ) : (
                                    <Upload className={`w-8 h-8 mb-2 transition-colors group-hover:-translate-y-1 ${modalHasBwa ? 'text-blue-900/50 group-hover:text-blue-400' : 'text-red-900/50 group-hover:text-red-400'}`} />
                                )}
                                <span className={`text-sm font-bold transition-colors ${modalHasBwa ? 'text-blue-400/70 group-hover:text-blue-400' : 'text-red-400/70 group-hover:text-red-400'}`}>
                                    {isUploading ? 'Dokumente werden hochgeladen...' : 'Neues PDF hier auswählen oder reinziehen'}
                                </span>
                            </label>
                        </div>

                        <div className="carbon-content flex justify-between items-center pt-2 border-t border-pe-border-strong">
                            <button 
                                type="button"
                                onClick={() => setYearNeedingUpload(null)} 
                                className="text-stone-500 dark:text-slate-500 hover:text-pe-text text-xs font-bold uppercase tracking-widest transition-colors underline"
                            >
                                {modalHasBwa ? 'Schließen & Zurück' : 'Später hochladen & Zurück'}
                            </button>

                            <button 
                                type="button"
                                onClick={() => setShowBwaModal(true)} 
                                className="inline-flex items-center gap-2 text-pe-accent-text hover:text-pe-text text-xs font-bold uppercase tracking-widest transition-colors"
                            >
                                <Download className="w-4 h-4" /> Muster-BWA ansehen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBwaModal && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="carbon-panel carbon-weave-bg border border-pe-border-strong rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col relative overflow-hidden">
                        <div className="carbon-content p-4 border-b border-pe-border-strong flex justify-between items-center bg-pe-tile-header">
                            <h3 className="text-pe-text font-bold flex items-center gap-2">
                                <FileText className="w-5 h-5 text-pe-accent-text" /> Muster-BWA (Beispiel)
                            </h3>
                            <button onClick={() => setShowBwaModal(false)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text p-2 metal-badge transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="carbon-content flex-1 bg-pe-sunken dark:bg-black p-2 relative">
                            <iframe 
                                src="/assets/muster-bwa.pdf" 
                                className="w-full h-full rounded-xl bg-white"
                                title="Muster BWA"
                            />
                        </div>
                        <div className="carbon-content p-4 border-t border-pe-border-strong bg-pe-tile-header flex justify-between items-center">
                            <a 
                                href="/assets/muster-bwa.pdf" 
                                download="Muster_BWA_PreisExpert.pdf"
                                className="text-pe-muted dark:text-slate-400 hover:text-pe-accent-text text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
                            >
                                <Download className="w-4 h-4" /> PDF Herunterladen
                            </a>
                            <button onClick={() => setShowBwaModal(false)} className="metal-badge hover:bg-pe-card text-pe-text px-6 py-2 rounded-lg text-sm font-bold transition-colors">
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* / BWA UPLOAD MODAL OVERLAY */}

            {/* STICKY HEADER (Kompakt) */}
            <div className="sticky top-0 z-[100] carbon-weave-bg bg-[#F4F2EF]/95 dark:bg-[#0a0a0a]/95 backdrop-blur-md border-b border-pe-border dark:border-[#222] shadow-2xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        
                        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-start">
                            <h1 className="text-xl sm:text-2xl font-black text-pe-text tracking-widest uppercase drop-shadow-lg m-0">Budgetplaner</h1>
                            
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => setYearNeedingUpload(Number(bwaCurrentYearStr))}
                                    className={`px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg border hover:-translate-y-0.5 whitespace-nowrap animate-in fade-in ${
                                        bwaList.length > 0 
                                        ? 'bg-pe-card text-pe-accent-text hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong' 
                                        : 'bg-red-900/50 text-red-400 hover:bg-red-800 border-red-500/50'
                                    }`}
                                    title="BWA Verwaltung öffnen"
                                >
                                    {bwaList.length > 0 ? <FileCheck size={14} /> : <Upload size={14} />}
                                    {bwaList.length > 0 ? 'BWA VERWALTEN' : 'BWA HOCHLADEN'}
                                </button>

                                {bwaList.length > 0 && bwaList.map((url, idx) => (
                                    <button 
                                        key={url}
                                        onClick={() => handleOpenBwa(url)} 
                                        className="bg-[#E6C299] text-black px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-[#d4ac7d] transition-all shadow-lg border border-[#b08d55] whitespace-nowrap"
                                        title={`BWA Datei ${idx + 1} öffnen`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
                                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM0,160H40V56H216V200ZM176,88a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,88Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,128Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,168Z"></path>
                                        </svg>
                                        {bwaList.length > 1 ? `BWA Teil ${idx + 1} ÖFFNEN` : `BWA ${bwaCurrentYearStr} ÖFFNEN`}
                                    </button>
                                ))}
                            </div>

                            {monthSelected && (
                                <button
                                    onClick={handleUnlockClick}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-widest transition-all shadow-inner border ml-2 ${
                                        isEditing 
                                            ? 'bg-pe-sunken text-stone-500 dark:text-gray-500 border-pe-border-strong hover:bg-pe-card' 
                                            : 'bg-pe-card text-orange-600 dark:text-orange-500 border-orange-500/50 hover:border-orange-400 hover:shadow-[0_0_15px_rgba(249,115,22,0.2)]'
                                    }`}
                                >
                                    {isEditing ? <Unlock size={14} /> : <Lock size={14} />}
                                    {isEditing ? "Bearbeitung aktiv" : "Gesperrt"}
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3 bg-pe-sunken py-2 px-4 rounded-xl border border-pe-border dark:border-[#222] shadow-inner w-full md:w-auto">
                            <span className="font-bold text-pe-muted dark:text-gray-400 uppercase tracking-widest text-[10px] hidden sm:inline">Zeitraum BWA:</span>
                            <div className="relative group flex items-center">
                                <Info className="h-4 w-4 text-stone-500 dark:text-gray-500 hover:text-pe-accent-text transition-colors cursor-help" />
                                <div className="absolute top-full right-0 md:left-1/2 md:-translate-x-1/2 mt-3 w-72 p-4 bg-pe-card border border-pe-border-strong text-pe-muted dark:text-gray-300 text-xs rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 leading-relaxed font-medium">
                                    <div className="absolute bottom-full right-4 md:left-1/2 md:-translate-x-1/2 border-[8px] border-transparent border-b-[#333]"></div>
                                    {monatsInfoText}
                                </div>
                            </div>
                            <SelectCell 
                                value={zeitraum} 
                                onChange={handleZeitraumChange}
                                options={monate}
                                placeholder="Wählen..."
                                className={`!h-9 !w-36 !text-xs !pl-3 !pr-8 ${!monthSelected ? 'blinking-border !border-red-500' : ''}`}
                            />
                            {!monthSelected && <span className="text-red-500 text-[10px] font-black uppercase tracking-widest animate-pulse ml-1">Pflichtfeld!</span>}
                        </div>
                        
                    </div>
                </div>
                {isEditing && (
                    <div className="w-full bg-gradient-to-r from-orange-600 to-orange-500 text-white text-center py-1.5 font-black uppercase text-[10px] tracking-widest shadow-[0_5px_20px_rgba(234,88,12,0.4)] border-b border-orange-400">
                        ACHTUNG: Bearbeitungsmodus aktiv für {zeitraum}
                    </div>
                )}
            </div>

            {/* CONTENT */}
            <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 md:px-8 py-8 relative">
                {monthSelected && (
                    <>
                        <div className="mb-8 text-sm text-pe-muted dark:text-gray-300 bg-pe-tile-header border border-pe-border dark:border-[#222] p-5 rounded-xl shadow-inner font-medium leading-relaxed">
                            <p>Bitte unbedingt den Monat der BWA auswählen - die Berechnung funktioniert nur von Feb. bis Nov.</p>
                            <p className="font-bold text-red-400 mt-2">Wichtig: Wenn Kosten sicher wegfallen (z. B. abbezahlter Kredit etc.), dann bitte 1€ eintragen, damit der Wert auch vom Jahresbudget abgezogen wird!</p>
                        </div>

                        <div className="relative">
                            {/* OVERLAY WENN NICHT IM EDIT-MODUS */}
                            {!isEditing && <LockedOverlay onUnlockClick={handleUnlockRequest} />}

                            <div className={`carbon-panel carbon-weave-bg shadow-2xl transition-all duration-300 ${!isEditing ? 'blur-[2px] opacity-70' : ''}`}>
                                <div className="carbon-content">
                                    <table className="w-full border-collapse">
                                        <thead className="bg-pe-sunken sticky top-0 z-20">
                                            <tr className="border-b border-pe-border dark:border-[#222]">
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-left w-48 z-10 border-r border-pe-border dark:border-[#1a1a1a]">Kostenart</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-32 border-r border-pe-border dark:border-[#1a1a1a]">Jahresbudget</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-32 border-r border-pe-border dark:border-[#1a1a1a]">Monatsbudget</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-pe-accent-text text-center w-48 border-r border-pe-border dark:border-[#1a1a1a] bg-stone-200/50 dark:bg-[#111]/50">Istwert aus BWA</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-32 border-r border-pe-border dark:border-[#1a1a1a]">Restbudget Jahr</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-32 border-r border-pe-border dark:border-[#1a1a1a]">Restbudget Monat</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-36 border-r border-pe-border dark:border-[#1a1a1a]">+/- Budget p. Monat</th>
                                                <th className="p-4 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-gray-500 text-center w-12"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-pe-tile-header">
                                            {calculations.berechneteDaten.map((item: any, idx: number) => {
                                                const isDevice = item.id.startsWith('geraetEintrag');
                                                const labelColor = isDevice ? 'text-purple-400' : (item.id === 'gewerbesteuer' || item.id === 'koerperschaftssteuer' ? 'text-green-500' : 'text-pe-muted dark:text-gray-300');
                                                const budgetPlansummeColor = item.id === 'budgetPlansumme' ? 'text-orange-500' : '';
                                                const isIncomeRow = item.isIncome;
                                                const isDisabled = !isEditing; 
                                                const isLast = idx === calculations.berechneteDaten.length - 1;
                                                
                                                return (
                                                    <React.Fragment key={item.id}>
                                                        <tr className={`h-16 align-middle border-b border-pe-border dark:border-[#1a1a1a] hover:bg-white/5 transition-colors ${isLast ? 'border-none' : ''}`}>
                                                            <td className="p-3 text-left align-middle text-sm border-r border-pe-border dark:border-[#1a1a1a]">
                                                                <div className="flex flex-col justify-center h-full pr-2">
                                                                    <span className={`font-bold ${labelColor} ${budgetPlansummeColor}`}>{item.label}</span>
                                                                    {item.isVariabel && (
                                                                        <span className="text-[9px] text-orange-500 font-black uppercase tracking-widest mt-1">
                                                                            variabel
                                                                        </span>
                                                                    )}
                                                                    {isDevice && (
                                                                        <span className="text-[9px] text-purple-500 font-black uppercase tracking-widest mt-1">
                                                                            Gerät
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a]"><ResultCell value={item.jahresbudget} currency={currency} /></td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a]"><ResultCell value={item.monatsbudget} currency={currency} /></td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a] bg-stone-200/30 dark:bg-[#111]/30">
                                                                <InputCell 
                                                                    value={item.istwertBwa} 
                                                                    onChange={(val: string) => handleIstwertChange(item.id, val)} 
                                                                    isLocked={isDisabled} 
                                                                    jahresbudget={item.jahresbudget}
                                                                    itemId={item.id}
                                                                    currency={currency}
                                                                />
                                                            </td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a]"><ResultCell value={item.restBudgetJahr} currency={currency} /></td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a]"><ResultCell value={item.restBudgetMonat} currency={currency} /></td>
                                                            <td className="p-2 border-r border-pe-border dark:border-[#1a1a1a]"><ResultCell value={item.plusMinusMonat} isNegativeRed={!isIncomeRow} currency={currency} /></td>
                                                            <td className="p-2 text-center">
                                                                <div className="flex justify-center items-center">
                                                                    {item.hatEingabe && item.plusMinusMonat !== 0 && (item.plusMinusMonat > 0 ? 
                                                                        <ThumbsUp className="h-5 w-5 text-green-500 drop-shadow-md" /> : <ThumbsDown className="h-5 w-5 text-red-500 drop-shadow-md" />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {item.id === 'material' && (
                                                            <tr className="h-3 bg-pe-input dark:bg-[#000]">
                                                                <td colSpan={8} className="p-0 border-y border-pe-border dark:border-[#222]"></td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <Section className="mt-10 mb-16 carbon-panel carbon-weave-bg border-none shadow-2xl">
                            <div className="carbon-content">
                                <SectionTitle className="flex justify-between items-center !border-pe-border dark:!border-[#222]">
                                    <span>Auswertung der Budgets für die letzten <span className="text-pe-text">{calculations.anzahlMonateBisher} Monate</span></span>
                                </SectionTitle>
                                <div className="p-6 rounded-b-2xl bg-pe-tile-header">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0">
                                        {/* COL 1 */}
                                        <div className="p-6 flex flex-col justify-between md:border-r border-pe-border dark:border-[#222] bg-pe-sunken rounded-xl md:rounded-none md:rounded-l-xl shadow-inner">
                                            <div className="h-10"></div>
                                            <div className="flex justify-between items-baseline mb-8 border-b border-pe-border dark:border-[#1a1a1a] pb-4">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">Plus/Minus pro Monat</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className={`text-2xl font-black font-mono drop-shadow-md ${calculations.totalPlusMinusMonat < 0 ? 'text-red-600 dark:text-red-500' : 'text-pe-accent-text'}`}>{formatEUR(calculations.totalPlusMinusMonat)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-baseline">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest text-left">Plus/Minus Budget<br/><span className="text-pe-text font-black">insgesamt</span></span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className={`text-2xl font-black font-mono drop-shadow-md ${calculations.plusMinusBudgetInsgesamt < 0 ? 'text-red-600 dark:text-red-500' : 'text-pe-accent-text'}`}>{formatEUR(calculations.plusMinusBudgetInsgesamt)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* COL 2 */}
                                        <div className="p-6 flex flex-col justify-between md:border-r border-pe-border dark:border-[#222] bg-pe-sunken rounded-xl md:rounded-none shadow-inner">
                                            <h3 className="font-black text-center text-sm mb-6 h-10 flex items-center justify-center text-pe-text uppercase tracking-widest">Rücklagenauswertung<br/>(Gesamtleistung)</h3>
                                            <div className="flex justify-between items-baseline mb-3 border-b border-pe-border dark:border-[#1a1a1a] pb-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">Pro Monat</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-bold text-pe-accent-text font-mono">{formatEUR(calculations.basisRuecklagenMonat)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-baseline mb-3 border-b border-pe-border dark:border-[#1a1a1a] pb-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">{calculations.anzahlMonateBisher} Monate</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-bold text-pe-accent-text font-mono">{formatEUR(calculations.basisRuecklagenMonat * calculations.anzahlMonateBisher)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-baseline pt-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">Prozent</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-2xl font-black text-pe-accent-text font-mono">{new Intl.NumberFormat('de-DE', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(calculations.basisRuecklagenProzent)}</span>
                                                    <span className="unit-text">%</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* COL 3 */}
                                        <div className="p-6 flex flex-col justify-between bg-pe-sunken rounded-xl md:rounded-none md:rounded-r-xl shadow-inner border border-pe-border dark:border-[#222] md:border-none">
                                            <h3 className="font-black text-center text-sm mb-6 h-10 flex items-center justify-center gap-3 text-pe-text uppercase tracking-widest">
                                                Reale Rücklagen
                                                <StatusIndicator percentage={calculations.realeRuecklagenProzent} />
                                            </h3>
                                            <div className="flex justify-between items-baseline mb-3 border-b border-pe-border dark:border-[#1a1a1a] pb-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">Pro Monat</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-bold text-pe-text font-mono">{formatEUR(calculations.realeRuecklagenMonat)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-baseline mb-3 border-b border-pe-border dark:border-[#1a1a1a] pb-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">{calculations.anzahlMonateBisher} Monate</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-bold text-pe-text font-mono">{formatEUR(calculations.realeRuecklagenInsgesamt)}</span>
                                                    <span className="unit-text">{currency}</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-baseline pt-2">
                                                <span className="text-xs text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest">Prozent</span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-2xl font-black text-pe-text font-mono drop-shadow-md">{new Intl.NumberFormat('de-DE', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(calculations.realeRuecklagenProzent)}</span>
                                                    <span className="unit-text">%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-t border-pe-border dark:border-[#222] mt-8 pt-6 text-center">
                                        <DynamicFeedback 
                                            totalPlusMinusMonat={calculations.totalPlusMinusMonat}
                                            realeRuecklagenProzent={calculations.realeRuecklagenProzent}
                                            externeRuecklagenProzent={calculations.basisRuecklagenProzent} 
                                            hasAnyInput={calculations.hasAnyInput}
                                            currency={currency}
                                            targetLiquiditaetProzent={targetLiquiditaetProzent}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Section>
                    </>
                )}
            </main>
        </div>
    );
};

export default BudgetPlanerPage;